import { useEffect, useMemo, useState } from 'react';

import AdSenseUnit from './AdSenseUnit';
import {
  canRenderAdsenseOnCurrentRuntime,
  fetchGoogleAdsConfig,
  normalizeGoogleAdsConfig,
} from '../../utils/google-ads';

const ENTRY_AD_COUNTER_KEY = 'liga.googleAds.entryCounter';
const ENTRY_AD_DISMISSED_KEY = 'liga.googleAds.entryDismissedAt';
const DISMISS_TTL_MS = 30 * 60 * 1000;

const readNumber = (key) => {
  const value = Number.parseInt(window.localStorage.getItem(key) || '0', 10);
  return Number.isFinite(value) ? value : 0;
};

const shouldShowEntryAd = (config) => {
  if (!canRenderAdsenseOnCurrentRuntime()) return false;
  if (
    !config.webEntryFullscreenEnabled ||
    !config.adsensePublisherId ||
    !config.webEntryFullscreenSlotId
  ) {
    return false;
  }

  const dismissedAt = readNumber(ENTRY_AD_DISMISSED_KEY);
  if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) {
    return false;
  }

  const nextCounter = readNumber(ENTRY_AD_COUNTER_KEY) + 1;
  window.localStorage.setItem(ENTRY_AD_COUNTER_KEY, String(nextCounter));

  return nextCounter % config.webEntryFullscreenFrequency === 0;
};

const GoogleAdsEntryInterstitial = () => {
  const [config, setConfig] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!canRenderAdsenseOnCurrentRuntime()) return undefined;

    let cancelled = false;
    fetchGoogleAdsConfig()
      .then((nextConfig) => {
        if (cancelled) return;
        const normalizedConfig = normalizeGoogleAdsConfig(nextConfig);
        setConfig(normalizedConfig);
        setOpen(shouldShowEntryAd(normalizedConfig));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  const canShow = useMemo(
    () =>
      open &&
      config?.adsensePublisherId &&
      config?.webEntryFullscreenSlotId &&
      canRenderAdsenseOnCurrentRuntime(),
    [config?.adsensePublisherId, config?.webEntryFullscreenSlotId, open]
  );

  const close = () => {
    window.localStorage.setItem(ENTRY_AD_DISMISSED_KEY, String(Date.now()));
    setOpen(false);
  };

  if (!canShow) return null;

  return (
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/80 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Publicidade"
    >
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-surface text-on-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-on-surface/10 px-4 py-3">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-on-surface/50">
            Publicidade
          </span>
          <button
            type="button"
            onClick={close}
            className="rounded-full border border-on-surface/20 px-3 py-1 text-xs font-semibold text-on-surface/70"
          >
            Fechar
          </button>
        </div>
        <AdSenseUnit
          publisherId={config.adsensePublisherId}
          slotId={config.webEntryFullscreenSlotId}
          format="auto"
          className="my-0 border-0"
          style={{ minHeight: 280 }}
          label="Publicidade"
        />
      </div>
    </div>
  );
};

export default GoogleAdsEntryInterstitial;
